import * as React from 'react';
import {
  DefaultButton,
  DefaultPalette,
  ICommandBarItemProps,
  PrimaryButton,
  TextField,
} from '@fluentui/react';
import TimeTaxSupportSystemClient from '../../system';
import MessageCourier from '../../lib/MessageCourier';
import ViewWrapperWithCommandBar from '../Layout/ViewWrapperWithCommandBar';
import { defaultCommandBarButtonStyles, titleCommandBarButtonStyles } from '../../styles/button-styles';
import DataGrid, {
  Column,
  Export,
  TotalItem,
  Summary,
  Paging,
  Scrolling,
} from 'devextreme-react/data-grid';
import { User } from '../../system/User';
import { EscalableExporterOptions, exportToExcel } from '../../lib/Util';
import { DateTime } from 'luxon';
import { OrganizationChartDropdown } from '../Common/OrganizationChartDropdown';
import useAsyncDataCourier from '../../common/custom-hooks/useAsyncDataCourier';

import useIsMobile from '../../common/custom-hooks/useIsMobile';
import CheckboxFilterComponent from '../../lib/CheckboxFilterComponent';
import { Company } from '../../system/company/Company';
import { Collaborator } from '../../system/collaborators/Collaborator';
import { OrganizationChartNode } from '../../system/organization_chart/OrganizationChartNode';
import Loading from '../Spinners/Loading';
import { t } from 'i18next';

interface IReportingBillableDistribution {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (state: boolean) => void;
  user: User;
  loading?: boolean;
}

interface CheckBoxOption {
  key: string;
  text: string;
  checked: boolean;
  title?: string,
  ariaLabel?: string,
  active?: "1" | "0";
}

export const ReportingBillableDistribution = (props: IReportingBillableDistribution) => {
  const [items, setItems] = React.useState<any[]>([]);
  const [emp_id, setEmp_id] = React.useState<string | undefined>(undefined);
  const [pro_codigo, setPro_codigo] = React.useState<string | undefined>(undefined);
  const [pro_nombre, setPro_nombre] = React.useState<string | undefined>(undefined);
  const [pro_owner_colid, setPro_owner_colid] = React.useState<string | undefined>(undefined);
  const [pro_norid, setPro_norid] = React.useState<string | undefined>(undefined);

  const [pro_list_companies, setPro_list_companies] = React.useState<Company[]>([]);
  const [pro_select_company, setPro_select_company] = React.useState<CheckBoxOption[]>([]);

  const [pro_list_owners, setPro_list_owners] = React.useState<Collaborator[]>([]);
  const [pro_select_owner, setPro_select_owner] = React.useState<CheckBoxOption[]>([]);

  const [pro_list_sectors, setPro_list_sectors] = React.useState<OrganizationChartNode[]|undefined>([]);
  const [pro_select_sector, setPro_select_sector] = React.useState<CheckBoxOption[]>([]);

  const [cleanComboBox, setCleanComboBox] = React.useState<boolean>(false);

  React.useEffect(() => {
    handleGetListCompany();
    handleGetListOwners();
    handleGetListSector();
  }, []);
  const { isMobile } = useIsMobile();

  const nodes = useAsyncDataCourier(
      () => {
        if (props.user) {
          return props.system.getOrganizationChartSystem().getAvailableNodesOf(props.user);
        }
        return Promise.resolve([]);
      },
      [props.system, props.user],
      props.courier
  );

  const itemsMenu = React.useMemo(
      (): ICommandBarItemProps[] => [
        {
          key: 'title',
          text: 'Reporte de distribución facturable',
          iconProps: {
            iconName: 'ReminderPerson',
            color: DefaultPalette.themeLighterAlt,
            className: 'text-title-btn',
          },
          buttonStyles: titleCommandBarButtonStyles,
          className: 'text-title-btn',
        },
      ],
      []
  );

  const onClickSearch = async () => {
    props.changeBlockUI(true);
    setCleanComboBox(false);
    try {
      const _items = await props.system
          .getReportingSystem()
          .getBillableDistributionReport(pro_codigo, pro_nombre, emp_id, pro_owner_colid, pro_norid);
      setItems(_items);
      props.courier.messageSuccess('Reporte generado con éxito');
    } catch (error) {
      props.courier.messageError(error);
    }
    props.changeBlockUI(false);
  };

  const onClickCleanFilters = () => {
    setEmp_id('');
    setPro_codigo('');
    setPro_nombre('');
    setPro_owner_colid('');
    setPro_norid('');
    setItems([]);
    setCleanComboBox(true);
  };

  const onExporting = (e: any) => {
    const day = DateTime.local();
    let options: EscalableExporterOptions = {
      fileName: `reporte-distribucion-facturable-${day.toFormat('dd-LL-yyyy')}`,
      sheetName: 'Reporte de distribucion facturable',

      customizeCell: (options) => {
        if (options.gridCell?.value === undefined && options.gridCell?.rowType !== 'header') {
          const nombre_empresa = options.gridCell?.column?.caption;
          const data = options.gridCell?.data;
          options.excelCell.value = data?.empresas[nombre_empresa!];
          if (+data?.empresas[nombre_empresa!] > 0) {
            options.excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'e1e2e3' } };
          } else {
            options.excelCell.value = '-';
          }
        }
        if (options.gridCell?.column?.name !== 'porcentaje_total' && +options.gridCell?.value > 0) {
          options.excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'e1e2e3' } };
        }
        if (options.gridCell?.column?.name !== 'porcentaje_total' && +options.gridCell?.value === 0) {
          options.excelCell.value = '-';
        }
        if (options.gridCell?.rowType === 'header') {
          options.excelCell.style = {
            border: {
              top: { style: 'medium', color: { argb: 'FF000000' } },
              left: { style: 'medium', color: { argb: 'FF000000' } },
              bottom: { style: 'medium', color: { argb: 'FF000000' } },
              right: { style: 'medium', color: { argb: 'FF000000' } },
            },
          };
          options.excelCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'b3b4b5' },
          };
          options.excelCell.alignment = { textRotation: 45 };
        }
      },
    };
    exportToExcel(e, options);
  };

  const handleGetListCompany = async () => {
    setPro_list_companies([]);
    setPro_select_company([]);

    const companySystem = props.system.getCompanySystem();
    const companies = await companySystem.getList([{ name: 'isActive', value: 'false' }]);

    setPro_list_companies(companies);

    const newCompanies: CheckBoxOption[] = companies.map((company) => ({
      key: company.getId(),
      text: company.getName(),
      checked: false,
      active: company.getActive() ? "1" : "0",
    }));

    setPro_select_company((prevList) => [...prevList, ...newCompanies]);
  }

  const handleApplyCompany = (selectedOptions) => {
    const checkedKeys = selectedOptions
      .filter((item) => item.checked)
      .map((item) => item.key);

    const filteredSecondArray = pro_list_companies.filter((item) => checkedKeys.includes(item.getId()));
    const idsString = filteredSecondArray.map((item) => item.getId()).join(",");
    setEmp_id(idsString);
  }

  const handleGetListOwners = async () => {
    setPro_list_owners([]);
    setPro_select_owner([]);

    const collaboratorSystem = props.system.getCollaboratorSystem();
    const collaborators = await collaboratorSystem.getCollaborators();

    setPro_list_owners(collaborators);

    const newCollaborators = collaborators.map((collaborator) => ({
      key: collaborator.getId(),
      text: collaborator.getName() + ' ' + collaborator.getLastName(),
      checked: false,
    }));

    setPro_select_owner((prevList) => [...prevList, ...newCollaborators]);
  }

  const handleApplyOwner = (selectedOptions) => {
    const checkedKeys = selectedOptions
      .filter((item) => item.checked)
      .map((item) => item.key);

    const filteredSecondArray = pro_list_owners.filter((item) => checkedKeys.includes(item.getId()));
    const idsString = filteredSecondArray.map((item) => item.getId()).join(",");
    setPro_owner_colid(idsString);
  }

  const handleGetListSector = async () => {
    setPro_select_sector([]);
    setPro_list_sectors([]);

    const sectorSystem = props.system.getOrganizationChartSystem()
    const sectors = await sectorSystem.getAvailableNodesOf(props.user);

    setPro_list_sectors(sectors);

    const newSectors = sectors?.map((node) => ({
      key: node.getId(),
      text: node.getName(),
      checked: false,
      title: node.getLevel().getName(),
      ariaLabel: node.parentPath(),
    }));

    setPro_select_sector((prevList) => [...prevList, ...(newSectors || [])]);
  }

  const handleApplySector = (selectedOptions) => {
    const checkedKeys = selectedOptions
      .filter((item) => item.checked)
      .map((item) => item.key);

    const filteredSecondArray = pro_list_sectors?.filter((item) => checkedKeys.includes(item.getId()));
    const idsString = filteredSecondArray?.map((item) => item.getId()).join(",");
    setPro_norid(idsString);
  }

  if (!props.loading) {
    return <Loading title='Cargando' description='Por favor espere estamos procesando la información necesaria para completar su solicitud.'/>
  }

  return (
      <>
        <ViewWrapperWithCommandBar
            withBorderBottom
            title={''}
            items={itemsMenu}
            overflowButtonStyles={defaultCommandBarButtonStyles}
            variant="highligth"
            titleBody={'Reporte de distribucion facturable'}
        >
          <div className={`${isMobile ? '': 'padding-1'} bg-white`}>
            <div className="ms-Grid" dir="ltr">
              <div className="ms-Grid-row mb-2">
                {
                  isMobile ? (
                    <div className="ms-Grid-col ms-md2 ms-sm12" style={{display:"flex", flexDirection:"row", justifyContent:"space-between", gap:"2px"}}>
                        <span style={{ alignSelf: "center", marginTop:"20px", marginRight:"3px", marginLeft:"7px"}}>Filtros</span>
                        <div style={{marginRight:"8px"}}>
                          <PrimaryButton
                              iconProps={{ iconName: 'Search' }}
                              className="mt-for-btn-search"
                              onClick={onClickSearch}
                              text={'Buscar'}
                          />
                          <DefaultButton
                              iconProps={{ iconName: 'Broom' }}
                              className="mt-for-btn-search ml-1"
                              onClick={onClickCleanFilters}
                              text={'Limpiar filtros'}
                          />
                        </div>
                    </div>
                  ) : null
                }
                </div>
                <div className="ms-Grid-col ms-md2 ms-sm6">
                  <TextField
                      label={t("Nombre del proyecto")}
                      value={pro_nombre}
                      onChange={(_ev, val) => setPro_nombre(val)}
                  />
                </div>
                <div className="ms-Grid-col ms-md2 ms-sm6">
                  <TextField
                      label={t("Codigo del proyecto")}
                      value={pro_codigo}
                      onChange={(_ev, val) => setPro_codigo(val)}
                  />
                </div>
                <div className="ms-Grid-col ms-md2 ms-sm6">
                  <CheckboxFilterComponent
                    title={t("Creador del proyecto")}
                    titleStyle = {{
                      label:{
                        color:"dark"
                      }
                    }}
                    initialOptions={pro_select_owner}
                    onApply={handleApplyOwner}
                    dropdownStyle={{
                      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                      borderRadius: "5px",
                      position:"fixed"
                    }}
                    isClean={cleanComboBox}
                    hiddenTooltip
                  />
                </div>
                <div className="ms-Grid-col ms-md2 ms-sm6">
                  {nodes && (
                    <>
                      <CheckboxFilterComponent
                        title={t("Sector")}
                        titleStyle = {{
                          label:{
                            color:"dark"
                          }
                        }}
                        initialOptions={pro_select_sector}
                        onApply={handleApplySector}
                        dropdownStyle={{
                          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                          borderRadius: "5px",
                          position:"fixed",
                          width:"500px"
                        }}
                        isClean={cleanComboBox}
                        hiddenTooltip
                      />
                    </>
                  )}
                </div>
                <div className="ms-Grid-col ms-sm6  ms-md2">
                  <CheckboxFilterComponent
                    title={t("Empresa")}
                    titleStyle = {{
                      label:{
                        color:"dark"
                      }
                    }}
                    initialOptions={pro_select_company}
                    onApply={handleApplyCompany}
                    dropdownStyle={{
                      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                      borderRadius: "5px",
                      position:"fixed"
                    }}
                    isClean={cleanComboBox}
                    hiddenTooltip
                  />
                </div>
                <div className="ms-Grid-col ms-md2"  style={{display: isMobile ? "none" : "flex", flexDirection:"row", gap:"5px"}}>
                  <PrimaryButton
                      iconProps={{ iconName: 'Search' }}
                      className="mt-for-btn-search"
                      onClick={onClickSearch}
                      text={t('Buscar')}
                  />
                  <DefaultButton
                      iconProps={{ iconName: 'Broom' }}
                      className="mt-for-btn-search"
                      onClick={onClickCleanFilters}
                      text={t('Limpiar filtros')}
                  />
                </div>
              </div>
              <div className="ms-Grid-row data-grid-container">
                {items.length > 0 && (
                    <DataGrid
                        height={620}
                        width="100%"
                        keyExpr={'proyecto_codigo'}
                        dataSource={items}
                        showBorders={true}
                        wordWrapEnabled={true}
                        onExporting={onExporting}
                    >
                      <Paging defaultPageSize={100} />
                      <Scrolling mode="virtual" showScrollbar="always" />
                      <Column
                          dataField="proyecto_nombre"
                          caption={t("Nombre")}
                          alignment="center"
                          fixed={true}
                          width="200px"
                      />
                      <Column
                          dataField="proyecto_inicio"
                          caption={t("Fecha inicio del proyecto")}
                          alignment="center"
                          fixed={true}
                          width="120px"
                          calculateCellValue={(row) => DateTime.fromFormat(row.proyecto_inicio, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')}
                      />
                      <Column
                          dataField="proyecto_fin"
                          caption={t("Fecha fin del proyecto")}
                          alignment="center"
                          fixed={true}
                          width="120px"
                          calculateCellValue={(row) => DateTime.fromFormat(row.proyecto_fin, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')}
                      />
                      <Column
                          dataField="proyecto_codigo"
                          caption={t("Codigo")}
                          alignment="center"
                          fixed={true}
                          width="100px"
                      />
                      <Column
                          dataField="proyecto_sector"
                          caption={t("Sector")}
                          alignment="center"
                          fixed={true}
                          width="150px"
                      />
                      <Column
                        caption={t("Responsable")}
                        alignment="center"
                        width="180px"
                        calculateCellValue={(row) =>row.proyecto_responsable ? `${row.proyecto_responsable.col_nombre} ${row.proyecto_responsable.col_apellido}` : "No tiene responsable" }
                      />

                      <Column dataField="proyecto_responsable.col_id_sap" caption={t("SAP responsable")} alignment="center" width="100px" />
                      <Column dataField="proyecto_creador" caption={t("Creador")} alignment="center" width="150px" />
                      <Column dataField="proyecto_estado" caption={t("Estado")} alignment="center" width="100px" />
                      <Column
                          dataField="porcentaje_total"
                          caption={t("Porcentaje total")}
                          alignment="center"
                          width="100px"
                      />
                      {Object.keys(items[0]['empresas']).map((empresa_nombre, ind) => {
                        return (
                            <Column
                                key={ind}
                                cellRender={(data) => {
                                  let value = '-';
                                  const empresa = Object.keys(data.data.empresas).find(
                                      (emp) => emp === empresa_nombre
                                  );
                                  if (empresa) {
                                    value = data.data.empresas[empresa_nombre];
                                  }
                                  return <span>{value}</span>;
                                }}
                                dataField={'empresas[' + empresa_nombre + ']'}
                                caption={empresa_nombre}
                                alignment="center"
                                width="100px"
                            />
                        );
                      })}
                      <Summary>
                        <TotalItem
                            column="proyecto_nombre"
                            summaryType="sum"
                            displayFormat={`${t('Total:')} ${items.length} ${t('resultados.')}`}
                        />
                      </Summary>
                      <Export enabled={true} />
                    </DataGrid>
                )}
              </div><br /><br /><br />
          </div>
        </ViewWrapperWithCommandBar>
      </>
  );
};
